import React from 'react'
import {
  Avatar,
  AvatarGroup,
  Card,
  CardBody,
  Flex,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react'
import { FiMoreVertical, FiUsers, FiEdit2, FiTrash2 } from 'react-icons/fi'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { Group, GroupMember } from '@/types'

const iconPalette = [
  'blue.500',
  'purple.500',
  'teal.500',
  'orange.500',
  'pink.500',
  'cyan.500',
]

function getGroupColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return iconPalette[Math.abs(hash) % iconPalette.length]
}

interface Props {
  group: Group
  members?: GroupMember[]
  isAdmin: boolean
  onManageMembers: () => void
  onEdit: () => void
  onDelete: () => void
}

export default function GroupCard({ group, members, isAdmin, onManageMembers, onEdit, onDelete }: Props) {
  const { t } = useAppTranslation(['admin', 'common'])
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const memberColor = useColorModeValue('gray.500', 'gray.400')

  const membersCount = group.members_count ?? members?.length ?? 0

  return (
    <Card
      borderRadius="xl"
      boxShadow="sm"
      border="1px solid"
      borderColor={borderColor}
      _hover={{ boxShadow: 'md' }}
      transition="all 0.2s"
    >
      <CardBody>
        <VStack align="stretch" spacing={4}>
          <HStack justify="space-between" align="flex-start">
            <Flex
              w={10}
              h={10}
              borderRadius="lg"
              bg={getGroupColor(group.name)}
              align="center"
              justify="center"
              flexShrink={0}
            >
              <Icon as={FiUsers} color="white" boxSize={5} />
            </Flex>
            <Menu placement="bottom-end">
              <MenuButton
                as={IconButton}
                icon={<FiMoreVertical />}
                variant="ghost"
                size="sm"
                aria-label="Group actions"
              />
              <MenuList minW="180px">
                <MenuItem icon={<FiUsers />} onClick={onManageMembers}>
                  {t('groups.manageMembers', { ns: 'admin' })}
                </MenuItem>
                {isAdmin && (
                  <>
                    <MenuDivider />
                    <MenuItem icon={<FiEdit2 />} onClick={onEdit}>
                      {t('groups.renameGroup', { ns: 'admin' })}
                    </MenuItem>
                    <MenuItem icon={<FiTrash2 />} color="red.500" onClick={onDelete}>
                      {t('groups.deleteGroup', { ns: 'admin' })}
                    </MenuItem>
                  </>
                )}
              </MenuList>
            </Menu>
          </HStack>

          <Text fontWeight="semibold" fontSize="lg" noOfLines={1}>
            {group.name}
          </Text>

          <HStack spacing={3}>
            {members && members.length > 0 ? (
              <AvatarGroup size="sm" max={3} spacing="-0.5rem">
                {members.map((member) => (
                  <Avatar
                    key={member.id}
                    name={member.user?.name}
                    bg="brand.500"
                    color="white"
                  />
                ))}
              </AvatarGroup>
            ) : (
              <AvatarGroup size="sm" max={3} spacing="-0.5rem">
                <Avatar bg="gray.300" />
              </AvatarGroup>
            )}
            <Text fontSize="sm" color={memberColor}>
              {membersCount} {t('groups.members', { ns: 'admin' })}
            </Text>
          </HStack>
        </VStack>
      </CardBody>
    </Card>
  )
}
